<template>
  <form
    class="form-registration"
    v-on:submit.prevent="onSubmit"
  >
    <div class="form-registration__row">
      <div class="form-registration__column">
        <v-text-field
          ref="userName"
          autocomplete="name"
          id="user-name"
          label="Имя"
          outlined
          placeholder="Имя"
          required="required"
          type="text"
          v-bind:class="{ invalid: userNameInputErrors.length > 0 }"
          v-letter-capital
          v-model="userName"
        />
        <div class="form-registration__input__error">
          <div
            class="form-registration__input__error__text"
            v-for="(error, index) in userNameInputErrors"
            v-bind:key="index">{{error}}</div>
        </div>
      </div>
      <div class="form-registration__column">
        <v-text-field
          autocomplete="family-name"
          label="Фамилия"
          outlined
          id="user-surname"
          placeholder="Фамилия"
          required="required"
          type="text"
          v-bind:class="{ invalid: userSurnameInputErrors.length > 0 }"
          v-letter-capital
          v-model="userSurname" />
        <div class="form-registration__input__error">
          <div
            class="form-registration__input__error__text"
            v-for="(error, index) in userSurnameInputErrors"
            v-bind:key="index">{{error}}</div>
        </div>
      </div>
    </div>
    <div class="field-row">
      <div class="form-registration__column">
        <v-text-field
          label="Название клиники"
          outlined
          autocomplete="organization"
          id="clinic-name"
          placeholder="Название клиники"
          required="required"
          type="text"
          v-bind:class="{ invalid: clinicNameInputErrors.length > 0 }"
          v-letter-capital
          v-model="clinicName" />
        <div class="form-registration__input__error">
          <div
            class="form-registration__input__error__text"
            v-for="(error, index) in clinicNameInputErrors"
            v-bind:key="index">{{error}}</div>
        </div>
      </div>
    </div>
    <div class="form-registration__row">
      <div class="form-registration__column">
        <input-phone
          title=""
          class="form-registration__phone"
          id="user-phone"
          v-bind:class="{ invalid: userPhoneInputErrors.length > 0 }"
          v-model="userPhone"
          v-on:focus.native="onInputPhoneFocus" />
        <div class="form-registration__input__error">
          <div
            class="form-registration__input__error__text"
            v-for="(error, index) in userPhoneErrors"
            v-bind:key="index">{{error}}</div>
        </div>
      </div>
      <div class="form-registration__column">
        <v-text-field
          label="E-mail"
          outlined
          autocomplete="email"
          id="user-email"
          placeholder="E-mail"
          required="required"
          type="email"
          v-bind:class="{ invalid: emailAddressInputErrors.length > 0 }"
          v-model="userEmail" />
        <div class="form-registration__input__error">
          <div
            class="form-registration__input__error__text"
            v-for="(error, index) in emailAddressInputErrors"
            v-bind:key="index">{{error}}</div>
        </div>
      </div>
    </div>
    <div class="form-registration__row button">
      <button
        class="form-registration__submit"
        type="submit"
      >Создать клинику</button>
      <div class="form-registration__legalinformation">
        Нажимая на кнопку «Зарегистрироваться»,
        я принимаю
        <router-link
          class="auth-page__link"
          target="_blank"
          v-bind:to="{ name: 'PrivacyPolicy' }"
        >положения политики конфиденциальности</router-link>
        и даю свое согласие на обработку персональных данных
      </div>
    </div>
  </form>
</template>
<script>
import InputPhone from '@/components/InputPhone.vue';
import { RegistrationRequest } from '@/storage/gateways/RegisterClinicGateway';
import { validatePhoneNumber } from '@/inputConstraint';
import violations from '@/violations.json';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Форма регистрации пользователя.
 * @type {any}
 */
export default {
  components: {
    InputPhone,
  },
  computed: {
    clinicNameInputErrors: resolveInputErrors('clinic_name'),
    emailAddressInputErrors: resolveInputErrors('user_email'),
    userNameInputErrors: resolveInputErrors('user_name'),
    userPhoneInputErrors: resolveInputErrors('user_phone'),
    userSurnameInputErrors: resolveInputErrors('user_surname'),
    /**
     * @description Ошибки при вводе номера телефона
     * @returns {Array<string>}
     */
    userPhoneErrors() {
      const errors = this.userPhoneInputErrors;
      const phone = this.userPhone.replaceAll(/\(|\)|-/ig, '').toString();
      if (phone.length < 10) {
        return errors;
      }
      const valid = validatePhoneNumber(phone);
      if (valid === false && errors.length === 0) {
        errors.push(violations.user_phone_number.format);
      }
      return errors;
    },
  },
  data() {
    return {
      /**
       * @description Название клиники.
       * @type {string}
       */
      clinicName: '',
      /**
       * @description Адрес электронной почты пользователя.
       * @type {string}
       */
      userEmail: '',
      /**
       * @description Имя пользователя.
       * @type {string}
       */
      userName: '',
      /**
       * @description Номер мобильного телефона пользователя.
       * @type {string}
       */
      userPhone: '',
      /**
       * @description Фамилия пользователя.
       * @type {string}
       */
      userSurname: '',
      /**
       * @description Сообщения ошибок мобильного телефона пользователя
       * @type {Array<string>}
       */
      userPhoneError: [],
    };
  },
  invalidFieldClass: 'invalid',
  methods: {
    getInvalidFieldClassesByViolationCauses(mappedCauseName) {
      const classes = [];
      if (this.violationCauses.includes(mappedCauseName)) {
        classes.push(this.$options.invalidFieldClass);
      }
      return classes;
    },
    onInputPhoneFocus() {
      this.userPhone = this.userPhone || '+7';
    },
    onSubmit() {
      const phone = this.userPhone.replaceAll(/\(|\)|-/ig, '');
      const request = new RegistrationRequest(
        this.clinicCity,
        this.clinicName,
        this.userEmail,
        this.userName,
        phone,
        this.userSurname,
      );
      this.$emit('registration-submit', request);
    },
  },
  mounted() {
    this.$refs.userName.focus();
  },
  name: 'FormRegistration',
  props: {
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    violationCauses: { type: Object, required: true },
  },
};
</script>
<style lang="scss">
@use "@/scss/buttons";
.form-registration {
  &__column {
    display: flex;
    flex-direction: column;
  }
  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 25px;
    &.button {
      align-items: center;
      margin: 10px 0 0 0;
      background-color: transparent;
    }
  }
  &__input,
  &__input.phone {
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    border: 1px solid #C2CFE0;
    height: 57px;
    width: 100%;
    padding: 0 15px;
    color: #192A3E;
    opacity: 0.7;
    margin: 0 0 30px 0;
    &:focus {
      color: #192A3E;
      background-color: #fff;
      border: 2px solid #0489E0;
      outline: 0;
    }
    &:disabled {
      background-color: #fff;
    }
    &__error {
      margin: -20px 0 30px 0;
      color: var(--warning-color);
      padding: 0;
      background-color: transparent !important;
      &__text {
        margin-top:5px;
        font-size: 12px;
        line-height: 12px;
      }
    }
  }
  &__phone {
    .input-phone__input {
      height: 57px !important;
      opacity: 0.7;
      padding: 0 15px;
      margin: 0 0 30px 0;
    }
    .input-phone__label {
      margin: 0;
    }
  }
  &__submit {
    @include buttons.btn-blue(100%, 70px, 20px);
  }
  &__legalinformation {
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: #707683;
    a {
      color: #707683 !important;
      &:hover {
        text-decoration: none;
      }
    }
  }
}
.v-text-field--outlined fieldset {
  border-color: #C2CFE0;
}
.v-text-field input {
  color: #192A3E !important;
}
@media screen and (max-width: 480px) {
  .form-registration {
    &__row {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 0;
    }
    &__submit {
     margin: 0 0 20px 0;
   }
  }
}
</style>
